import { useEffect, useState } from 'react'
import { connectedParticipantsObserver } from '@livekit/components-core'
import { RemoteParticipant, RoomEvent } from 'livekit-client'

import { useMaybeRoomContext } from '@/src/context'

/**
 * Делит удалённых участников на тех, кто уже в комнате, и тех, кто в зале.
 *
 * Список зала — проекция прав LiveKit, а не отдельный стор. Раньше обычные
 * комнаты слушали события вручную и переподписывались на каждый `.size`,
 * из‑за этого строка у организатора могла висеть после ухода гостя.
 * `connectedParticipantsObserver` тот же, что в HR: connect/disconnect
 * и смена прав приходят одним потоком.
 */
export const useParticipantsList = () => {
  const roomContext = useMaybeRoomContext()
  const [activeParticipants, setActiveParticipants] = useState<RemoteParticipant[]>([])
  const [waitingParticipants, setWaitingParticipants] = useState<RemoteParticipant[]>([])

  useEffect(() => {
    if (!roomContext) {
      return
    }

    /**
     * Раскладывает участников по правам publish/subscribe.
     *
     * Скрытых и агентов в список зала не кладём — орга их не видит.
     */
    const participantDistribution = (participants: Iterable<RemoteParticipant>) => {
      const nextActive: RemoteParticipant[] = []
      const nextWaiting: RemoteParticipant[] = []

      for (const participant of participants) {
        if (!participant.permissions?.hidden && !participant.permissions?.agent) {
          if (participant.permissions?.canSubscribe) {
            nextActive.push(participant)
          }

          if (!participant.permissions?.canSubscribe && !participant.permissions?.canPublish) {
            nextWaiting.push(participant)
          }
        }
      }

      setActiveParticipants(nextActive)
      setWaitingParticipants(nextWaiting)
    }

    const subscription = connectedParticipantsObserver(roomContext, {
      additionalRoomEvents: [RoomEvent.ParticipantPermissionsChanged],
    }).subscribe(participantDistribution)

    return () => subscription.unsubscribe()
  }, [roomContext])

  return { activeParticipants, waitingParticipants }
}
